import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Users, Loader2, Eye, EyeOff, KeyRound, Download, UserPlus, Copy, Link2, Mail, LogOut } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

const ROLE_LABELS = {
  admin: { label: "Administrador", color: "bg-purple-500" },
  operator: { label: "Operador", color: "bg-blue-500" },
  quality: { label: "Qualidade", color: "bg-green-500" },
  shift_leader: { label: "Chefe de Turno", color: "bg-amber-500" },
};

const SHIFT_LABELS = {
  morning: "Manhã",
  afternoon: "Tarde",
  night: "Noite",
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showPins, setShowPins] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteRole, setInviteRole] = useState("operator");
  const [inviteShift, setInviteShift] = useState("morning");
  const [inviteHours, setInviteHours] = useState(72);
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [formData, setFormData] = useState({
    user_code: "",
    name: "",
    pin: "",
    role: "operator",
    shift: "morning",
    function: "",
  });
  const [editFormData, setEditFormData] = useState({
    name: "",
    pin: "",
    role: "operator",
    shift: "morning",
    function: "",
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erro ao carregar utilizadores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => {
    setFormData({
      user_code: "",
      name: "",
      pin: "",
      role: "operator",
      shift: "morning",
      function: "",
    });
  };

  const exportLoginsPDF = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Credenciais de Acesso - EP3D ENGINEERING PROJECT</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px; }
          .header-left { display: flex; align-items: center; gap: 20px; }
          .logo { height: 60px; }
          .header h1 { font-size: 22px; color: #2563eb; }
          .header-right { text-align: right; }
          .header-right p { color: #64748b; font-size: 12px; }
          .warning { background: #fef3c7; border: 1px solid #f59e0b; padding: 15px; border-radius: 8px; margin-bottom: 25px; }
          .warning h3 { color: #92400e; font-size: 14px; margin-bottom: 5px; }
          .warning p { color: #92400e; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; }
          th { background: #2563eb; color: white; font-weight: 600; }
          tr:nth-child(even) { background: #f8fafc; }
          .role-admin { background: #8b5cf6; color: white; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
          .role-operator { background: #3b82f6; color: white; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
          .role-quality { background: #22c55e; color: white; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
          .role-shift_leader { background: #f59e0b; color: white; padding: 2px 8px; border-radius: 4px; font-size: 11px; }
          .pin { font-family: 'Courier New', monospace; font-size: 16px; font-weight: bold; letter-spacing: 2px; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 15px; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${LOGO_URL}" alt="Logo" class="logo" />
            <div>
              <h1>Credenciais de Acesso</h1>
              <p style="color: #64748b; font-size: 14px;">EP3D ENGINEERING PROJECT</p>
            </div>
          </div>
          <div class="header-right">
            <p><strong>Data:</strong> ${new Date().toLocaleDateString('pt-PT')}</p>
            <p><strong>Total Utilizadores:</strong> ${users.length}</p>
          </div>
        </div>

        <div class="warning">
          <h3>⚠️ DOCUMENTO CONFIDENCIAL</h3>
          <p>Este documento contém informação sensível. Guarde em local seguro e não partilhe com pessoas não autorizadas.</p>
        </div>

        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome</th>
              <th>PIN</th>
              <th>Perfil</th>
              <th>Turno</th>
            </tr>
          </thead>
          <tbody>
            ${users.map(user => `
              <tr>
                <td><strong>${user.user_code}</strong></td>
                <td>${user.name}</td>
                <td class="pin">${user.pin}</td>
                <td><span class="role-${user.role}">${ROLE_LABELS[user.role]?.label || user.role}</span></td>
                <td>${SHIFT_LABELS[user.shift] || user.shift || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>EP3D ENGINEERING PROJECT - Sistema de Gestão de Produção</p>
          <p>Documento gerado automaticamente em ${new Date().toLocaleString('pt-PT')}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_code || !formData.name || !formData.pin) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (formData.pin.length !== 6) {
      toast.error("O PIN deve ter 6 dígitos");
      return;
    }

    try {
      await axios.post(`${API}/auth/register`, {
        user_code: formData.user_code,
        name: formData.name,
        pin: formData.pin,
        role: formData.role,
        shift: formData.shift,
        function: formData.function || null,
      });
      toast.success("Utilizador criado com sucesso");
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      showError(error, "Erro ao criar utilizador");
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name,
      pin: "", // PIN vazio - só preencher se quiser alterar
      role: user.role,
      shift: user.shift || "morning",
      function: user.function || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser) return;

    // Validar PIN se foi preenchido
    if (editFormData.pin && editFormData.pin.length !== 6) {
      toast.error("O PIN deve ter 6 dígitos");
      return;
    }

    try {
      const payload = {
        user_code: editingUser.user_code, // Não alteramos o código
        name: editFormData.name,
        pin: editFormData.pin || editingUser.pin, // Usar o novo ou manter o antigo
        role: editFormData.role,
        shift: editFormData.shift,
        function: editFormData.function || null,
      };

      await axios.put(`${API}/users/${editingUser.id}`, payload);
      toast.success("Utilizador atualizado com sucesso");
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      showError(error, "Erro ao atualizar utilizador");
    }
  };

  const handleEditPinInput = (value) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    setEditFormData({ ...editFormData, pin: numericValue });
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Tem a certeza que deseja eliminar o utilizador "${userName}"?`)) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Utilizador eliminado");
      fetchUsers();
    } catch (error) {
      showError(error, "Erro ao eliminar utilizador");
    }
  };

  const toggleShowPin = (userId) => {
    setShowPins(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handlePinInput = (value) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    setFormData({ ...formData, pin: numericValue });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Gestão de Utilizadores
          </h1>
          <p className="text-muted-foreground mt-1">
            Adicione, visualize e elimine utilizadores do sistema
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowInviteDialog(true)}
            data-testid="generate-invite-btn"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Gerar Link de Convite
          </Button>
          <Button
            variant="outline"
            onClick={exportLoginsPDF}
            data-testid="export-logins-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Logins (PDF)
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("Terminar turno de TODOS os colaboradores em serviço?\n\nEsta acção fecha os registos de ponto de todos os turnos.")) return;
              try {
                const r = await axios.post(`${API}/time-tracking/end-all-shifts`);
                if (r.data.count === 0) toast.info("Ninguém em serviço no momento");
                else toast.success(`${r.data.count} colaborador(es) com saída registada`);
              } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
            }}
            data-testid="end-all-shifts-btn"
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Fim de Turno Geral
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Novo Utilizador
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo text-foreground">Novo Utilizador</DialogTitle>
              <DialogDescription>
                Crie um novo utilizador para o sistema
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: João Silva"
                  required
                  data-testid="user-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="user_code">Código *</Label>
                  <Input
                    id="user_code"
                    value={formData.user_code}
                    onChange={(e) => setFormData({ ...formData, user_code: e.target.value.toUpperCase() })}
                    placeholder="Ex: OP001"
                    required
                    className="font-mono"
                    data-testid="user-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pin">PIN (6 dígitos) *</Label>
                  <Input
                    id="pin"
                    type="password"
                    value={formData.pin}
                    onChange={(e) => handlePinInput(e.target.value)}
                    placeholder="••••••"
                    required
                    maxLength={6}
                    className="font-mono tracking-widest"
                    data-testid="user-pin"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Perfil *</Label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="user-role"
                  >
                    <option value="operator">Operador</option>
                    <option value="quality">Qualidade</option>
                    <option value="shift_leader">Chefe de Turno</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shift">Turno *</Label>
                  <select
                    id="shift"
                    value={formData.shift}
                    onChange={(e) => setFormData({ ...formData, shift: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="user-shift"
                  >
                    <option value="morning">Manhã</option>
                    <option value="afternoon">Tarde</option>
                    <option value="night">Noite</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="function">Função</Label>
                <Input
                  id="function"
                  value={formData.function}
                  onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                  placeholder="Ex: Operador de Máquina"
                  data-testid="user-function"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-user-btn">
                  Criar Utilizador
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-3xl font-bold text-foreground">{users.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Operadores</p>
              <p className="text-3xl font-bold text-blue-600">
                {users.filter(u => u.role === "operator").length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Qualidade</p>
              <p className="text-3xl font-bold text-green-600">
                {users.filter(u => u.role === "quality").length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Administradores</p>
              <p className="text-3xl font-bold text-purple-600">
                {users.filter(u => u.role === "admin").length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
            <Users className="h-5 w-5" />
            Lista de Utilizadores
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>PIN</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Turno</TableHead>
                  <TableHead>Função</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const roleConfig = ROLE_LABELS[user.role] || ROLE_LABELS.operator;
                  return (
                    <TableRow key={user.id} data-testid={`user-row-${user.user_code}`}>
                      <TableCell className="font-mono font-bold text-foreground">
                        {user.user_code}
                      </TableCell>
                      <TableCell className="text-foreground">{user.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-foreground">
                            {showPins[user.id] ? (user.pin || "******") : "••••••"}
                          </span>
                          <button
                            onClick={() => toggleShowPin(user.id)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showPins[user.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${roleConfig.color} text-white`}>
                          {roleConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground">
                        {SHIFT_LABELS[user.shift] || user.shift}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.function || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditUser(user)}
                            className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                            data-testid={`edit-user-${user.user_code}`}
                            title="Editar utilizador"
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user.id, user.name)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`delete-user-${user.user_code}`}
                            title="Eliminar utilizador"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* PIN Info Box */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <KeyRound className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Informação sobre PINs</p>
                <p className="text-sm text-amber-700 mt-1">
                  Por segurança, todos os PINs são armazenados de forma encriptada. 
                  O PIN padrão para novos utilizadores é <span className="font-mono font-bold">123456</span>.
                  Os utilizadores devem alterar o PIN após o primeiro acesso.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-chivo text-foreground">
              Editar Utilizador - {editingUser?.user_code}
            </DialogTitle>
            <DialogDescription>
              Altere os dados do utilizador. Deixe o PIN em branco para manter o atual.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nome Completo *</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                placeholder="Nome do utilizador"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-pin">Novo PIN (deixe em branco para manter)</Label>
              <div className="relative">
                <Input
                  id="edit-pin"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={editFormData.pin}
                  onChange={(e) => handleEditPinInput(e.target.value)}
                  placeholder="••••••"
                  maxLength={6}
                  className="font-mono tracking-widest text-center text-lg"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {editFormData.pin.length}/6
                </div>
              </div>
              <p className="text-xs text-amber-600">
                ⚠️ Se preencher, o PIN será alterado para o novo valor
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-role">Perfil</Label>
                <select
                  id="edit-role"
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                >
                  <option value="operator">Operador</option>
                  <option value="quality">Qualidade</option>
                  <option value="shift_leader">Chefe de Turno</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-shift">Turno</Label>
                <select
                  id="edit-shift"
                  value={editFormData.shift}
                  onChange={(e) => setEditFormData({ ...editFormData, shift: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                >
                  <option value="morning">Manhã</option>
                  <option value="afternoon">Tarde</option>
                  <option value="night">Noite</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-function">Função</Label>
              <Input
                id="edit-function"
                value={editFormData.function}
                onChange={(e) => setEditFormData({ ...editFormData, function: e.target.value })}
                placeholder="Ex: Operador de Máquina"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" data-testid="update-user-btn">
                Guardar Alterações
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invitation Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={(open) => { setShowInviteDialog(open); if (!open) setGeneratedInvite(null); }}>
        <DialogContent className="max-w-lg" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <UserPlus className="h-5 w-5" />
              {generatedInvite ? "Convite Gerado" : "Gerar Link de Convite"}
            </DialogTitle>
            <DialogDescription>
              {generatedInvite
                ? "Envie este link ao novo utilizador. So pode ser usado uma vez."
                : "O link permite ao destinatario criar a sua propria conta com PIN seguro."}
            </DialogDescription>
          </DialogHeader>
          
          {!generatedInvite ? (
            <div className="space-y-4 mt-3">
              <div>
                <Label>Perfil do novo utilizador</Label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  data-testid="invite-role-select"
                >
                  <option value="operator">Operador</option>
                  <option value="quality">Qualidade</option>
                  <option value="shift_leader">Chefe de Turno</option>
                  <option value="admin">Administrador</option>
                  <option value="display">Display (TV/Ecrã)</option>
                </select>
              </div>
              <div>
                <Label>Turno predefinido</Label>
                <select
                  value={inviteShift}
                  onChange={(e) => setInviteShift(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  data-testid="invite-shift-select"
                >
                  <option value="morning">Manhã</option>
                  <option value="afternoon">Tarde</option>
                  <option value="night">Noite</option>
                </select>
              </div>
              <div>
                <Label>Validade (horas)</Label>
                <Input
                  type="number"
                  min="1"
                  max="720"
                  value={inviteHours}
                  onChange={(e) => setInviteHours(parseInt(e.target.value) || 72)}
                  data-testid="invite-hours-input"
                />
                <p className="text-xs text-gray-500 mt-1">Padrão: 72h (3 dias). Após este prazo, o link expira.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancelar</Button>
                <Button
                  onClick={async () => {
                    setGeneratingInvite(true);
                    try {
                      const res = await axios.post(`${API}/invitations`, {
                        role: inviteRole,
                        shift: inviteShift,
                        expires_in_hours: inviteHours,
                      });
                      // Construir URL absoluta no frontend (porque PUBLIC_BASE_URL pode nao estar no backend)
                      const fullUrl = `${window.location.origin}/invite/${res.data.token}`;
                      setGeneratedInvite({ ...res.data, url: fullUrl });
                    } catch (e) {
                      showError(e, "Erro a gerar convite");
                    } finally {
                      setGeneratingInvite(false);
                    }
                  }}
                  disabled={generatingInvite}
                  data-testid="invite-generate-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {generatingInvite ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
                  Gerar Link
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-3">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                <p className="text-xs font-bold text-blue-700 uppercase mb-1">Link de Convite</p>
                <div className="flex gap-2 items-center">
                  <Input
                    value={generatedInvite.url}
                    readOnly
                    className="font-mono text-xs"
                    data-testid="invite-url-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedInvite.url);
                      toast.success("Link copiado!");
                    }}
                    size="sm"
                    data-testid="invite-copy-btn"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Perfil</p>
                  <p className="font-bold">{generatedInvite.role}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Expira em</p>
                  <p className="font-bold">{generatedInvite.expires_in_hours}h ({new Date(generatedInvite.expires_at).toLocaleString("pt-PT")})</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <strong>Como enviar:</strong> Copie o link e envie por email, WhatsApp ou SMS ao novo utilizador. Ele abre o link, escolhe código + nome + PIN, e a conta fica criada.
              </div>
              <div className="flex justify-between gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    const subject = "Convite EP3D MES - Criar conta";
                    const body = `Olá,%0A%0AFoi convidado a criar uma conta no Sistema MES da EP3D.%0A%0AClique no link abaixo para criar a sua conta (válido por ${generatedInvite.expires_in_hours} horas):%0A%0A${encodeURIComponent(generatedInvite.url)}%0A%0ACumprimentos,%0AAdministrador EP3D`;
                    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
                  }}
                  data-testid="invite-email-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />Enviar por Email
                </Button>
                <Button onClick={() => { setShowInviteDialog(false); setGeneratedInvite(null); }} data-testid="invite-close-btn">Fechar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
